import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import type { JSX } from "react";
import { useStore } from "zustand";
import { useQuery } from "@tanstack/react-query";
import {
  buildAskUserPairings,
  isAskUserName,
  resolveDecisionForToolCall,
  selectPendingPromptQueue,
  wrapAskUserAnswer,
  type ApprovalReadState,
  type PendingPrompt,
} from "@magnis/client-core";
import { useAgentChat } from "../modules/episodes/hooks/useAgentChat";
import { useAnalytics } from "../analytics/provider";
import type { AskUserPayload, PendingToolCall } from "../modules/episodes/types";
import { isSuperseded } from "./supersede";
import { AskUserCard } from "../components/agent/AskUserCard";
import { PendingPromptPanel } from "../components/agent/PendingPromptPanel";
import type {
  AgentHistoryBlock,
  AgentRendererProps,
  AllowlistScope,
  ToolCallRendererPayload,
} from "../runtime/contracts";
import { useAppRuntime } from "../providers/AppRuntimeProvider";
import { useAllowlist } from "../modules/settings/hooks/useAllowlist";
import { useModelSelector } from "../modules/settings/hooks/useModelSelector";
import { useRpcQuery } from "../runtime/hooks/useRpc";
import { AgentStatsRegion } from "../panels";
import { EpisodeAgentHeader } from "./EpisodeAgentHeader";
import { useEpisodeAgent } from "./useEpisodeAgent";
import {
  AgentDockedComposer,
  AgentLandingComposer,
  AgentMessageList,
  ToolApprovalCard,
} from "../components/agent/AgentPanelBlocks";
import type { PendingAttachment } from "../components/agent/ComposerPanels";
import { ToolCallCard } from "../components/agent/ToolCallCard";
import { isHiddenTool, toolResultError } from "../components/agent/toolCallHelpers";
import { Icon } from "../components/ui";
import { ContextMenu } from "../components/ui/ContextMenu";
import type { ContextMenuEntry } from "../components/ui/ContextMenu";
import { uploadFile } from "../hooks/fileUpload";
import { formatTimeAgo } from "../utils/time";
import { useEpisodeDetailQuery } from "../modules/episodes/queries";
import { buildContextPanelProps, getSourceVisual } from "../modules/episodes/helpers";
import { useEntityLinkedEpisodes, type EpisodeLinkSummary } from "../modules/episodes/hooks/useEntityLinkedEpisodes";
import { mergeContextPanelEpisodes, ContextPanel } from "../panels";
import {
  autoApprovalAttemptKey,
  shouldRenderHistoricalAllowlistCard,
  startAllowlistedApproval,
} from "./historicalAllowlist";
import { freshEpisodeKey } from "./freshEpisodeKey";
import { schemaIdToModuleId } from "./schemaIdToModuleId";
import { invalidateEpisodeContextQueries } from "../panels";
import { useWorkspaceStore } from "../store/workspaceStore";
import { useLiveAgentBilling } from "./liveAgentBilling";
import { formatUsdMicros } from "./liveAgentBilling";

export interface AgentPanelProps {
  readonly moduleName: string;
  readonly conversationKey?: string;
  readonly selectedItemId?: string;
  readonly selectedItemTitle?: string;
  readonly selectedChatId?: string;
  readonly selectedChatName?: string;
  readonly contextTitle?: string;
  /** When true, all pending tool calls are rendered as superseded (no Send buttons). */
  readonly archived?: boolean;
  /**
   * When set, this episode is loaded directly by id on first mount instead of
   * going through `list_for_entity`. Required when the caller already knows
   * which episode to display (e.g. Episodes module, where the URL carries the
   * episode UUID and the episode may not link to any target entity).
   */
  readonly presetEpisodeId?: string;
}

const agentChatStartedKeys = new Set<string>();

function reportAnalyticsCaptureError(error: unknown): void {
  // eslint-disable-next-line no-console
  console.error("analytics capture failed", error);
}

export function AgentPanel({
  moduleName,
  conversationKey,
  selectedItemId,
  selectedChatId,
  selectedChatName,
  archived,
  presetEpisodeId,
}: AgentPanelProps): JSX.Element {
  const baseContextKey =
    conversationKey ??
    `${moduleName}:${selectedChatId ?? selectedItemId ?? "_default"}`;
  // A "new chat" started while an episode is mid-run gets its OWN key, so the
  // running episode's stream keeps going in the background instead of being
  // aborted (the chat store keys streams by contextKey, so reusing the entity
  // key would abort + complete the in-flight episode). Cleared when the
  // chat/entity changes, so navigating back shows that chat's latest episode.
  const [overrideEpisodeKey, setOverrideEpisodeKey] = useState<string | null>(null);
  const contextKey = overrideEpisodeKey ?? baseContextKey;
  const isContentMode = Boolean(conversationKey);

  const runtime = useAppRuntime();
  const analytics = useAnalytics();
  const selectedWorkspaceId = useWorkspaceStore((state) => state.selectedWorkspaceId);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [submittedAskUser, setSubmittedAskUser] = useState<Set<string>>(new Set());
  const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showContext, setShowContext] = useState(false);
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);

  // When the chat/entity itself changes, drop any "new chat" override so we
  // fall back to that entity's latest episode (and don't carry a stale fresh
  // key across chats).
  useEffect(() => {
    setOverrideEpisodeKey(null);
  }, [baseContextKey]);

  // Reset pending attachments, history and context when switching context (different chat/entity)
  const prevContextKeyRef = useRef(contextKey);
  useEffect(() => {
    if (prevContextKeyRef.current !== contextKey) {
      prevContextKeyRef.current = contextKey;
      setPendingAttachments([]);
      setShowHistory(false);
      setShowContext(false);
      // Drop optimistic ask_user submissions — the new context has its own
      // tool_call IDs, any held-over entries would leak suppression.
      setSubmittedAskUser(new Set());
    }
  }, [contextKey]);

  const {
    messages,
    streamingContent,
    isStreaming,
    toolCalls,
    toolResults,
    contentBlocks,
    error,
    episodeId,
    episodeTitle,
    sendMessage,
    stopStream,
    approveToolCall: handleApproveToolCall,
    denyToolCall: handleDenyToolCall,
  } = useAgentChat(contextKey, selectedChatId ?? selectedItemId, presetEpisodeId);

  // replyTo for composer bar: ONLY from runtime store (user action).
  // Episode linked reply_to is shown in ContextPanel, not in composer.
  const replyTo = useStore(runtime.agent.store, (s) => s.replyTo);

  const { getMatchingEntry, addEntry, removeMatchingEntries } = useAllowlist();
  // The GLOBAL default a new conversation starts on. Not this episode's
  // engine — that is `useEpisodeAgent`, mounted in the panel header, and
  // conflating the two is how a user changes one and watches the other move.
  const { items: selectorItems, current: currentSelection, select: handleSelectionChange } = useModelSelector();
  // DEC-8: the Todo/Context transparency region is builtin-only. Gate on
  // agent.get_engine (persisted default, pure backend read) — NOT
  // agent.engines, which proxies the sidecar and 502s whenever the sidecar
  // is down, hiding the region for the wrong reason.
  const { data: engineData } = useRpcQuery<{ engine: string | null }>(
    ["agent.get_engine"],
    "agent.get_engine",
    {},
    { retry: 3, retryDelay: 1000 },
  );
  const agentEngineCurrent = engineData?.engine ?? null;

  const isLiveEmpty =
    messages.length === 0 &&
    !isStreaming &&
    contentBlocks.length === 0 &&
    !streamingContent;
  const {
    disabledReason: agentDisabledReason,
    limit: billingLimit,
  } = useLiveAgentBilling(
    isStreaming,
    error,
  );

  // Episode detail for context panel
  const { data: episodeDetail, isLoading: episodeDetailLoading, isError: episodeDetailError } = useEpisodeDetailQuery(episodeId ?? undefined);
  // The EPISODE's agent and model — distinct from useModelSelector above,
  // which owns the global default a new conversation starts on.
  const episodeAgent = useEpisodeAgent(runtime, episodeId ?? null, episodeDetail?.engine ?? null);
  // Hypothesis rows name two entities; the panel already knows the ones this
  // episode is linked to, and an unknown id renders as itself rather than as
  // a blank.
  const entityNameOf = useCallback(
    (id: string): string =>
      episodeDetail?.linked_entities.find((e) => e.id === id)?.name ?? id,
    [episodeDetail],
  );
  const currentEntityId = selectedChatId ?? selectedItemId;
  const toolResultRefreshKey = useMemo(
    () => toolResults.map((result) => result.id).join("|"),
    [toolResults],
  );
  useEffect(() => {
    if (!toolResultRefreshKey) return;
    invalidateEpisodeContextQueries(runtime.queryClient, episodeId ?? undefined, currentEntityId);
  }, [toolResultRefreshKey, runtime.queryClient, episodeId, currentEntityId]);
  // Plan #5: episodes linked to the entity on screen (graph-driven).
  const { data: entityLinkedEpisodes } = useEntityLinkedEpisodes(currentEntityId);

  // Plan #8: episode history for this context, sourced from the entity graph
  // (`episodes.list_for_entity`) instead of the removed `context_key` column.
  // For ephemeral surfaces (no entity), history is disabled — nothing to link by.
  const {
    data: historyRows,
    isLoading: historyLoading,
    isError: historyError,
  } = useQuery({
    queryKey: ["episodes", "history_for_entity", currentEntityId ?? null],
    queryFn: () =>
      runtime.transport.rpc<readonly EpisodeLinkSummary[]>(
        "episodes.list_for_entity",
        { entity_id: currentEntityId, limit: 100, offset: 0 },
      ),
    enabled: showHistory && Boolean(currentEntityId),
    staleTime: 10_000,
  });
  const historyEpisodes = useMemo(
    () => (historyRows ?? []).filter((row) => row.status !== "completed"),
    [historyRows],
  );
  const contextPanelProps = useMemo(() => {
    if (!episodeDetail) return null;
    // Derive source-module icon from `started_with` target's schema_id (plan #8).
    const startedWith = episodeDetail.linked_entities.find((e) => e.link_kind === "started_with");
    const launchedByTrigger = startedWith?.schema_id === "triggers.trigger";
    const sourceModuleId = schemaIdToModuleId(startedWith?.schema_id);
    const visuals = getSourceVisual(sourceModuleId);
    // Exclude the current entity from linked entities — it's already obvious from the sidebar context
    const filteredEntities = currentEntityId
      ? episodeDetail.linked_entities.filter((e) => e.id !== currentEntityId)
      : episodeDetail.linked_entities;
    const panelProps = buildContextPanelProps(
      {
        id: `agent:${episodeDetail.id}`,
        title: episodeDetail.title,
        preview: "",
        time: "",
        activityAt: null,
        iconName: visuals.icon,
        color: visuals.color,
        sourceModuleId,
        targetModuleId: sourceModuleId,
        targetItemId: episodeDetail.id,
      },
      filteredEntities,
    );

    // Plan #5: merge graph-driven episode list for the entity on screen.
    // Fallback: if currentEntityId is unavailable, use today's linked_entities unchanged.
    const graphRows = (entityLinkedEpisodes ?? []).map((row) => ({
      episode_id: row.episode_id,
      title: row.title,
      status: row.status,
      // Rebuilt field by field, so a field added only to the Rust struct dies
      // right here — silently, since the row still type-checks without it.
      is_archived: row.is_archived,
      link_kinds: row.link_kinds,
      updated_at: row.updated_at,
      isEmpty: row.isEmpty,
    }));
    // Current episode's other linked entities are ALREADY in panelProps.connectedEntities
    // as plain entity cards — we only add episode entries (from graph) and the current
    // episode self-reference.
    const mergedEpisodes = mergeContextPanelEpisodes({
      fromEntityGraph: graphRows,
      fromCurrentEpisode: [
        { episode_id: episodeDetail.id, link_kinds: ["child_episodes"] },
      ],
    });

    // Never show the on-screen entity in its own Context Panel: drop both the
    // currently-open episode and the entity id the panel is rendered for.
    const otherEpisodes = mergedEpisodes.filter(
      (row) => row.episode_id !== episodeDetail.id && row.episode_id !== currentEntityId,
    );
    void launchedByTrigger;
    return {
      ...panelProps,
      connectedEntities: [
        ...otherEpisodes.map((row) => ({
          id: row.episode_id,
          schemaId: "episodes.episode",
          name: row.title,
          linkKind: row.link_kinds[0] ?? "child_episodes",
        })),
        ...panelProps.connectedEntities,
      ],
    };
  }, [episodeDetail, currentEntityId, entityLinkedEpisodes]);

  const handleSelectEpisode = useCallback(
    (episodeId: string) => {
      setShowHistory(false);
      void runtime.agent.chat.loadEpisodeById(contextKey, episodeId);
    },
    [runtime, contextKey],
  );

  // Follow an episode the BACKEND is driving.
  //
  // A turn this panel started streams back over the agent stream, so its events
  // land in the store as they happen. A turn the backend starts — the trigger
  // executor calling stream_chat — has no response stream to ride on, so the
  // panel showed the trigger task and then nothing: no tool cards while the
  // agent worked, and no approval card when it parked one. Reopening the chat
  // was the only way to see any of it.
  //
  // The backend already broadcasts `episode_changed`; nothing was listening.
  // Reload on it, debounced, and only when this panel is not itself streaming —
  // a local turn owns its own updates and must not be yanked out from under.
  useEffect(() => {
    if (episodeId === null) return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const off = runtime.transport.onEventType(["episode_changed"], (event) => {
      // The bus event travels INSIDE `payload`: the WS wraps it as
      // `{type:"event", event_type:"episode_changed", payload:{…}}` and
      // parseEvent hands back `{type, payload, timestamp}`. Reading `event.id`
      // gets undefined and the guard below then swallows every event — which is
      // exactly how the first version of this subscription did nothing at all.
      const payload = (event as { payload?: { id?: unknown } }).payload;
      if (payload?.id !== episodeId) return;
      if (isStreaming) return;
      clearTimeout(timer);
      timer = setTimeout(() => {
        void runtime.agent.chat.loadEpisodeById(contextKey, episodeId);
      }, 350);
    });
    return (): void => {
      clearTimeout(timer);
      off();
    };
  }, [runtime, contextKey, episodeId, isStreaming]);

  // Scroll to bottom when new content arrives — and when a chat is opened.
  //
  // Setting scrollTop in the same commit reads a scrollHeight the browser has
  // not finished computing: markdown blocks, code and cards are still being
  // laid out, so the number is short and the feed lands part-way up. Opening a
  // chat looked like it scrolled to a random place for exactly that reason.
  // Two frames — one for React to commit, one for layout to settle — and the
  // height is final. `episodeId` is a dependency so switching chats always
  // bottoms out, even when the message array happens to compare equal.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let second = 0;
    const first = requestAnimationFrame(() => {
      second = requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
    return (): void => {
      cancelAnimationFrame(first);
      cancelAnimationFrame(second);
    };
  }, [messages, streamingContent, contentBlocks, episodeId]);

  // Auto-approve tool calls that are on the allowlist
  const autoApprovedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const tc of toolCalls) {
      if (tc.status !== "pending") continue;
      const attemptKey = autoApprovalAttemptKey(episodeId, tc.id);
      if (autoApprovedRef.current.has(attemptKey)) continue;
      const target = runtime.agent.resolveAllowlistTarget(tc);
      if (
        target &&
        getMatchingEntry(
          target.action,
          target.targetType,
          target.targetId,
          episodeId,
        )
      ) {
        startAllowlistedApproval(
          episodeId,
          tc.id,
          autoApprovedRef.current,
          async () => handleApproveToolCall(tc.id),
        );
      }
    }
  }, [toolCalls, getMatchingEntry, handleApproveToolCall, runtime, episodeId]);

  const modulePrompt = useMemo(
    () => runtime.agent.resolveSystemPrompt(moduleName),
    [runtime, moduleName],
  );

  const handleAttachFile = useCallback(async () => {
    const result = await uploadFile(runtime.transport);
    if (result) {
      setPendingAttachments((prev) => [...prev, { id: result.id, name: result.name, mimeType: result.mimeType }]);
    }
  }, [runtime.transport]);

  const handleRemoveAttachment = useCallback((id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  // Shared pending-prompt queue + decision resolver — moves actionable tool_calls
  // out of the feed into the pinned PendingPromptPanel and swaps resolved ones
  // for a compact DecisionSummary block.
  const approvalState: ApprovalReadState = useMemo(
    () => ({ toolCalls: [...toolCalls], toolResults: [...toolResults], messages, contentBlocks }),
    [toolCalls, toolResults, messages, contentBlocks],
  );
  const pendingQueue = useMemo(
    () => selectPendingPromptQueue(approvalState, {
      archived: archived === true,
      submittedAskUser,
      hasRenderer: (tc) => {
        const block: AgentHistoryBlock = {
          id: tc.id,
          kind: "tool_call",
          toolName: tc.name,
          payload: { args: tc.args, status: tc.status },
        };
        return runtime.agent.resolveHistoryRenderer(block) !== null;
      },
    }),
    [approvalState, archived, submittedAskUser, runtime],
  );
  const skipToolCallIds = useMemo(
    () => new Set(pendingQueue.map((p) => p.toolCall.id)),
    [pendingQueue],
  );
  const askUserPairings = useMemo(
    () => buildAskUserPairings(approvalState),
    [approvalState],
  );
  const resolveDecision = useCallback(
    (toolCallId: string) => {
      const decision = resolveDecisionForToolCall(approvalState, toolCallId);
      const toolCall = toolCalls.find((candidate) => candidate.id === toolCallId);
      if (!toolCall) return decision;

      const target = runtime.agent.resolveAllowlistTarget(toolCall);
      const hasMatchingGrant =
        target !== null &&
        getMatchingEntry(
          target.action,
          target.targetType,
          target.targetId,
          episodeId,
        ) !== undefined;

      return shouldRenderHistoricalAllowlistCard(
        decision,
        toolCall.status,
        hasMatchingGrant,
      )
        ? null
        : decision;
    },
    [approvalState, toolCalls, runtime, getMatchingEntry, episodeId],
  );
  const headPrompt = pendingQueue[0];
  const headPromptIsAskUser = headPrompt?.kind === "ask_user";
  const headPromptToolCallId = headPrompt?.toolCall.id;

  // Handle ask_user answer submission (shared by AskUserCard + composer fallthrough)
  const handleAskUserSubmit = useCallback(
    (toolCallId: string, formattedAnswer: string) => {
      setSubmittedAskUser((prev) => new Set(prev).add(toolCallId));
      sendMessage(formattedAnswer, {
        activeModule: moduleName,
        selectedEntityId: selectedItemId,
        selectedChatId,
        selectedChatName,
      }, undefined, undefined, modulePrompt);
    },
    [sendMessage, moduleName, selectedItemId, selectedChatId, selectedChatName, modulePrompt],
  );

  // eslint-disable-next-line @typescript-eslint/consistent-type-imports
  const onSend = useCallback((text: string, mentionList: readonly import("../modules/episodes/types").EntityMention[]) => {
    if (!text.trim()) return;
    const attachmentCount = mentionList.length + (replyTo ? 1 : 0);
    const baseAgentAnalytics = {
      module_id: moduleName.trim().toLowerCase().replace(/\s+/g, "_"),
      moduleName: moduleName,
      hasEntityContext: selectedItemId != null || selectedChatId != null || replyTo != null,
      hasModelSelection: currentSelection != null,
      mentionCount: mentionList.length,
      attachmentCount: attachmentCount,
      ...(analytics.hostedDemoEntitlement?.status
        ? { entitlementStatus: analytics.hostedDemoEntitlement.status }
        : {}),
      ...(analytics.hostedDemoEntitlement?.campaignId
        ? { campaignId: analytics.hostedDemoEntitlement.campaignId }
        : {}),
      ...(analytics.hostedDemoEntitlement?.inviteId
        ? { inviteId: analytics.hostedDemoEntitlement.inviteId }
        : {}),
      ...(analytics.hostedDemoEntitlement?.creditLimitMicros != null
        ? { credit_limit_micros: analytics.hostedDemoEntitlement.creditLimitMicros }
        : {}),
    };
    if (agentDisabledReason) {
      return;
    }
    const chatStartedKey = [
      analytics.clientInstanceId,
      analytics.analyticsFlowId,
      selectedWorkspaceId ?? "no-workspace",
      contextKey,
    ].join(":");
    if (!agentChatStartedKeys.has(chatStartedKey)) {
      agentChatStartedKeys.add(chatStartedKey);
      void analytics.capture(
        "agent_chat_started",
        {
          ...baseAgentAnalytics,
          workspacePresent: selectedWorkspaceId != null,
        },
        { source: "agent_composer" },
      ).catch(reportAnalyticsCaptureError);
    }
    void analytics.capture(
      "agent_message_sent",
      baseAgentAnalytics,
      { source: "agent_composer" },
    ).catch(reportAnalyticsCaptureError);
    // Composer fallthrough: when an ask_user prompt is at the head of the
    // pending queue, wrap free-form text in the marker so the agent treats it
    // like a card-submitted answer. Approval prompts remain binary — composer
    // text is not routed to them.
    if (headPromptIsAskUser && headPromptToolCallId !== undefined) {
      handleAskUserSubmit(headPromptToolCallId, wrapAskUserAnswer(text));
      setPendingAttachments([]);
      return;
    }
    const attachments: { schemaId: string; data: { id: string; [key: string]: unknown } }[] = mentionList.map((m) => ({
      schemaId: m.schemaId,
      data: { id: m.id, name: m.name },
    }));
    // Include replyTo entity as attachment so agent sees it
    if (replyTo) {
      attachments.push({
        schemaId: replyTo.schemaId,
        data: { id: replyTo.entityId, name: replyTo.name, ...replyTo.data, _replyTo: true },
      });
    }
    sendMessage(text, {
      activeModule: moduleName,
      selectedEntityId: selectedItemId,
      selectedChatId,
      selectedChatName,
      ...(replyTo ? { replyToEntityId: replyTo.entityId } : {}),
    // An episode's engine and model come from the EPISODE, and the backend
    // overwrites whatever a request names with what the episode is bound to.
    // Sending the global picker's value alongside made the composer's agent
    // control a knob that moved nothing on a bound episode — worse than
    // absent, because it looked live. The global selection still seeds a NEW
    // conversation, through `episodes.create`.
    }, attachments.length > 0 ? attachments : undefined, undefined, modulePrompt);
    // Clear replyTo after send
    if (replyTo) {
      runtime.agent.setReplyTo(null);
    }
    // Clear pending attachments after send
    setPendingAttachments([]);
  }, [
    analytics,
    contextKey,
    currentSelection,
    handleAskUserSubmit,
    headPromptIsAskUser,
    headPromptToolCallId,
    agentDisabledReason,
    moduleName,
    modulePrompt,
    replyTo,
    runtime,
    selectedChatId,
    selectedChatName,
    selectedItemId,
    selectedWorkspaceId,
    sendMessage,
  ]);

  // Allowlist toggle handler
  const handleAllowlistToggle = useCallback(
    (tc: PendingToolCall, scope?: AllowlistScope) => {
      const target = runtime.agent.resolveAllowlistTarget(tc);
      if (!target) return;
      const matchingEntry = getMatchingEntry(
        target.action,
        target.targetType,
        target.targetId,
        episodeId,
      );
      if (matchingEntry) {
        void removeMatchingEntries(
          target.action,
          target.targetType,
          target.targetId,
          episodeId,
        );
        return;
      }
      if (!scope) return;
      if (scope === "dialog" && !episodeId) return;
      void addEntry(
        target.action,
        target.targetType,
        target.targetId,
        target.targetLabel,
        scope === "dialog" ? episodeId : null,
      );
    },
    [runtime, getMatchingEntry, episodeId, addEntry, removeMatchingEntries],
  );

  // Draft edit handler — dispatches through contribution registry
  const handleEditDraft = useCallback(
    (tc: PendingToolCall) => {
      const args = tc.args as Record<string, unknown>;
      const block: AgentHistoryBlock = {
        id: tc.id,
        kind: "tool_call",
        toolName: tc.name,
        payload: args,
      };
      const reg = runtime.agent.resolveHistoryRenderer(block);
      if (reg) {
        runtime.agent.requestDraft({
          targetModuleId: reg.moduleId,
          payload: args,
        });
      }
    },
    [runtime],
  );

  // Render a single tool call inline — contribution-based dispatch with fallback
  const renderToolCall = useCallback(
    (tc: PendingToolCall): ReactNode => {
      const result = toolResults.find((tr) => tr.id === tc.id);

      // A pending tool call is superseded if archived, or a later pending call
      // shares its (name + recipient identity). The recipient identity is
      // resolved through the existing contribution seam (resolveAllowlistTarget →
      // each plugin's extractAllowlistTarget; chat_id for telegram sends), not a
      // hardcoded tool-name list — one source of truth. Keying on the recipient,
      // not just the name, keeps a legitimate multi-recipient fan-out from
      // collapsing into all-but-one superseded (DEC-5 / INV-6).
      const superseded = isSuperseded(
        tc,
        toolCalls,
        archived === true,
        (t) => {
          const target = runtime.agent.resolveAllowlistTarget(t)?.targetId;
          if (target != null && target !== "") return target;
          // Fallback recipient identity: a `chat_id` arg. set_trigger / reply carry
          // one but have NO allowlist target, so without this two set_triggers to
          // DIFFERENT chats collapse to name-only matching and the earlier shows a
          // false "Superseded". Keep them distinct by chat.
          const cid = (t.args as Record<string, unknown> | undefined)?.chat_id;
          return typeof cid === "string" || typeof cid === "number" ? String(cid) : "";
        },
      );

      // Try contribution-based rendering
      const block: AgentHistoryBlock = {
        id: tc.id,
        kind: "tool_call",
        toolName: tc.name,
        payload: { args: tc.args, status: tc.status },
      };
      const registration = runtime.agent.resolveHistoryRenderer(block);
      if (registration) {
        const target = runtime.agent.resolveAllowlistTarget(tc);
        const matchingEntry = target
          ? getMatchingEntry(
              target.action,
              target.targetType,
              target.targetId,
              episodeId,
            )
          : undefined;
        const allowed =
          matchingEntry === undefined
            ? false
            : matchingEntry.episode_id === null
              ? "always"
              : "dialog";
        const Render = registration.Render as ComponentType<AgentRendererProps<ToolCallRendererPayload>>;
        return (
          <Render
            key={tc.id}
            payload={{
              toolCall: tc,
              toolResult: result,
              isAllowlisted: allowed,
              superseded,
              selectedChatName,
              onApprove: async (argumentsOverride?: unknown) => handleApproveToolCall(tc.id, argumentsOverride),
              onDeny: async () => handleDenyToolCall(tc.id),
              onEdit: () => { handleEditDraft(tc); },
              onAllowlistToggle: (scope?: AllowlistScope) => {
                handleAllowlistToggle(tc, scope);
              },
            }}
            runtime={runtime}
            agent={runtime.agent}
          />
        );
      }

      // ask_user — interactive question card
      if (isAskUserName(tc.name)) {
        // Superseded ask_user should never be submittable from the feed —
        // the newer prompt is the live one (pinned in the panel).
        if (superseded) {
          return (
            <div
              key={tc.id}
              data-testid={`ask-user-superseded-${tc.id}`}
              className="rounded-lg border border-agent-border bg-surface-secondary px-3 py-2 text-[12px] text-agent-text-dim"
            >
              Earlier question — superseded by a newer prompt.
            </div>
          );
        }
        const payload = tc.args as AskUserPayload | undefined;
        if (payload?.tabs) {
          // Answered when either the optimistic submission set holds this id
          // or the shared per-id pairing finds a marker user_message AFTER
          // this ask_user's tool_call block in contentBlocks.
          const alreadyAnswered = submittedAskUser.has(tc.id) || askUserPairings.has(tc.id);
          return (
            <AskUserCard
              key={tc.id}
              payload={payload}
              submitted={alreadyAnswered}
              onSubmit={(answer) => { handleAskUserSubmit(tc.id, answer); }}
            />
          );
        }
      }

      // Fallback: approval card only when backend explicitly requested approval
      if (tc.status === "pending" && tc.approvalId) {
        return (
          <ToolApprovalCard
            key={tc.id}
            toolCall={tc}
            onApprove={() => handleApproveToolCall(tc.id)}
            onDeny={() => handleDenyToolCall(tc.id)}
          />
        );
      }

      // Internal / harness plumbing — hidden from the transcript. These are
      // not user-meaningful actions: episode bookkeeping (set_title,
      // append_message) and the Claude Code schema loader (ToolSearch), which
      // also leaks raw `mcp__…__` tool ids in its args.
      if (isHiddenTool(tc.name)) {
        return null;
      }

      // Collapsible tool call card — shows args + result on expand. A tool
      // that THREW carries a __tool_error marker → terminal error card, never
      // a perpetual spinner.
      const cardStatus = result
        ? (toolResultError(result.result) ? "error" : "complete")
        : "pending";
      return (
        <ToolCallCard
          key={tc.id}
          name={tc.name}
          args={tc.args}
          result={result?.result}
          status={cardStatus}
          runtime={runtime}
        />
      );
    },
    [
      runtime,
      toolCalls,
      toolResults,
      selectedChatName,
      getMatchingEntry,
      episodeId,
      handleApproveToolCall,
      handleDenyToolCall,
      handleEditDraft,
      handleAllowlistToggle,
      submittedAskUser,
      askUserPairings,
      handleAskUserSubmit,
      archived,
    ],
  );

  const renderPrompt = useCallback(
    (prompt: PendingPrompt): ReactNode => renderToolCall(prompt.toolCall),
    [renderToolCall],
  );

  const moreMenuItems: readonly ContextMenuEntry[] = useMemo(() => [
    { id: "show-context", label: "Show context", icon: "panel-right" as const },
  ], []);

  const handleMoreMenuSelect = useCallback((itemId: string) => {
    setMenuPos(null);
    if (itemId === "show-context") {
      setShowHistory(false);
      setShowContext(true);
    }
  }, []);

  const showLanding = isLiveEmpty && !isContentMode;

  const handleClearReplyTo = useCallback(() => {
    runtime.agent.setReplyTo(null);
  }, [runtime]);

  const composerProps = {
    isStreaming,
    onSend,
    onStop: stopStream,
    selectorItems,
    currentSelection,
    onSelectionChange: handleSelectionChange,
    replyTo,
    onClearReplyTo: handleClearReplyTo,
    runtime,
    onAttachFile: handleAttachFile,
    pendingAttachments,
    onRemoveAttachment: handleRemoveAttachment,
    disabledReason: agentDisabledReason,
  };

  return (
    <div className="flex flex-1 min-h-0 min-w-0 h-full flex-col overflow-hidden bg-transparent text-content">
      {/* Header — only shown in sidebar mode; DetailPane provides its own TopBarHeader */}
      {!isContentMode && (<>
        <header className="shrink-0 flex items-center justify-between h-[52px] px-5">
          <div className="flex items-center gap-2 min-w-0">
            {(showContext || showHistory) && (
              <button
                type="button"
                onClick={() => { setShowContext(false); setShowHistory(false); }}
                title="Back to chat"
                className="w-6 h-6 rounded-lg flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface-hover text-content-secondary shrink-0"
              >
                <Icon name="arrow-left" size={16} />
              </button>
            )}
            <span className="truncate text-[15px] font-semibold text-content">
              {showContext ? "Context" : showHistory ? "History" : (episodeTitle ?? "New chat")}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => {
                setShowHistory(false);
                setShowContext(false);
                // Start the new episode under a fresh key so any in-flight
                // episode on this chat keeps streaming in the background
                // instead of being aborted/completed.
                const freshKey = freshEpisodeKey(baseContextKey);
                void runtime.agent.chat.startNewEpisode(freshKey);
                setOverrideEpisodeKey(freshKey);
              }}
              title="New chat"
              className="w-8 h-8 rounded-lg flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface-hover text-content-secondary"
            >
              <Icon name="plus" size={18} />
            </button>
            <button
              type="button"
              onClick={() => { setShowContext(false); setShowHistory((v) => !v); }}
              title={showHistory ? "Back to chat" : "Episode history"}
              className={`w-8 h-8 rounded-lg flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface-hover ${showHistory ? "text-content" : "text-content-secondary"}`}
            >
              <Icon name={showHistory ? "arrow-left" : "clock-3"} size={18} />
            </button>
            <button
              type="button"
              onClick={(e) => { setMenuPos(menuPos ? null : { x: e.clientX, y: e.clientY }); }}
              title="More options"
              className="w-8 h-8 rounded-lg flex items-center justify-center bg-transparent border-none cursor-pointer hover:bg-surface-hover text-content-secondary"
            >
              <Icon name="ellipsis-vertical" size={18} />
            </button>
          </div>
        </header>
        {menuPos && (
          <ContextMenu
            items={moreMenuItems}
            position={menuPos}
            onSelect={handleMoreMenuSelect}
            onClose={() => { setMenuPos(null); }}
          />
        )}
      </>)}

      <div className="flex flex-1 min-w-0 min-h-0 flex-col">
        {showContext ? (
          <div className="flex flex-1 flex-col overflow-y-auto">
            {!episodeId && (
              <div className="py-10 px-4 text-center text-content-tertiary text-sm">
                No context available
              </div>
            )}
            {episodeId && episodeDetailLoading && (
              <div className="py-10 px-4 text-center text-content-tertiary text-sm">
                Loading...
              </div>
            )}
            {episodeId && episodeDetailError && (
              <div className="py-10 px-4 text-center text-content-tertiary text-sm">
                Failed to load context
              </div>
            )}
            {episodeId && contextPanelProps && (
              <ContextPanel
                hideHeader
                {...contextPanelProps}
                runtime={runtime}
                headerSlot={
                  <EpisodeAgentHeader
                    engine={episodeDetail?.engine ?? null}
                    engineLocked={episodeDetail?.engineLocked ?? false}
                    engines={episodeAgent.engines}
                    models={episodeAgent.models}
                    model={episodeDetail?.model ?? null}
                    lastTurnResolution={episodeDetail?.last_turn_resolution ?? null}
                    error={episodeAgent.error}
                    onSelectEngine={episodeAgent.selectEngine}
                    onSelectModel={episodeAgent.selectModel}
                  />
                }
                bottomSlot={
                  <AgentStatsRegion
                    runtime={runtime}
                    contextKey={contextKey}
                    episodeId={episodeId}
                    engineCurrent={agentEngineCurrent}
                    primaryEntityId={contextPanelProps.primaryEntity?.id ?? null}
                    nameOf={entityNameOf}
                  />
                }
              />
            )}
          </div>
        ) : showHistory ? (
          <div className="flex flex-1 flex-col overflow-y-auto">
            {historyLoading && (
              <div className="py-10 px-4 text-center text-content-tertiary text-sm">
                Loading...
              </div>
            )}
            {historyError && (
              <div className="py-10 px-4 text-center text-content-tertiary text-sm">
                Failed to load history
              </div>
            )}
            {!historyLoading && !historyError && historyEpisodes.length === 0 && (
              <div className="py-10 px-4 text-center text-content-tertiary text-sm">
                No previous conversations
              </div>
            )}
            {!historyLoading && !historyError && historyEpisodes.map((ep) => (
              <button
                key={ep.episode_id}
                type="button"
                onClick={() => { handleSelectEpisode(ep.episode_id); }}
                className={`w-full text-left px-5 py-3 border-none bg-transparent cursor-pointer hover:bg-surface-hover flex items-center gap-3 ${ep.episode_id === episodeId ? "bg-surface-hover" : ""}`}
              >
                <Icon name="message-circle" size={16} className="text-content-tertiary shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="truncate text-sm text-content">
                    {ep.title || "Untitled"}
                  </div>
                  <div className="text-xs text-content-tertiary mt-0.5">
                    {formatTimeAgo(ep.updated_at)}
                  </div>
                </div>
              </button>
            ))}
          </div>
        ) : showLanding ? (
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="min-h-0 flex-1" />
            <div className="shrink-0 px-1.5 pb-3">
              <AgentLandingComposer {...composerProps} />
              {/* @tested-by: tst_fe_scn_cloud_demo_040 */}
              {analytics.runtimeSurface === "hosted_demo" &&
              billingLimit?.credit_limit_micros !== null &&
              billingLimit?.credit_limit_micros !== undefined ? (
                <p className="mt-2 text-center text-xs text-content-tertiary">
                  {formatUsdMicros(billingLimit.credit_limit_micros)} of model budget · hosted by us
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <AgentMessageList
              messages={messages}
              streamingContent={streamingContent}
              isStreaming={isStreaming}
              error={error}
              contentBlocks={contentBlocks}
              toolCalls={toolCalls}
              toolResults={toolResults}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onApproveToolCall={handleApproveToolCall}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onDenyToolCall={handleDenyToolCall}
              renderToolCall={renderToolCall}
              scrollRef={scrollRef}
              runtime={runtime}
              stickyBg={isContentMode ? "bg-surface-tertiary" : "bg-surface-secondary"}
              skipToolCallIds={skipToolCallIds}
              resolveDecision={resolveDecision}
            />
            <PendingPromptPanel queue={pendingQueue} renderPrompt={renderPrompt} />
            <AgentDockedComposer {...composerProps} />
          </>
        )}
      </div>
    </div>
  );
}
